import {FC, useEffect, useState} from 'react'
import styles from './fitContent.module.css'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import {ModalWrapper} from '../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import {Input} from '../../../../components/Input/Input'
import useModals from '../../../../hooks/useModals'
import {DELETE, GET, PATCH, POST} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {useNavigate} from 'react-router-dom'

type QuestionResponseType = {
  id: number
  prompt: string
  completion: string
  created: string
  status: boolean
}

type QuestionType = {
  id: number
  prompt: string
  completion: string
  created: string
  status: boolean
}

type MetaType = {
  hasNextPage: boolean
  hasPreviousPage: boolean
  itemCount: number
  page: number
  pageCount: number
  take: number
}

type PluginconfigType = {
  id: number
  name: string
  content: any
  plugintemplateId: number
  plugintypeId: number
}

type TemplateType = {
  id: number
  name: string
  image: string | null
  parameters: string
  plugintypeId: number
  pluginconfig: PluginconfigType
  typeIntegration: string
  able: boolean | null
}

const FitContent: FC = () => {
  const navigate = useNavigate()
  const {showWrapperModal, showWarningModal} = useModals()

  const [questions, setQuestions] = useState<QuestionType[]>([])
  const [config, setConfig] = useState<PluginconfigType | null>(null)
  const [id, setId] = useState<number | null>(null)
  const [question, setQuestion] = useState<string>('')
  const [answer, setAnswer] = useState<string>('')
  const [agentName, setAgentName] = useState('')
  const [action, setAction] = useState<'creating' | 'editing' | 'deleting'>('creating')
  const [loading, setLoading] = useState(false)
  const [isActivePage, setIsActivePage] = useState(true);
  const [processId, setProcessId] = useState<string | null>(null);
  const [showFixedToolbar, setShowFixedToolbar] = useState(false);
  const [questionCharCount, setQuestionCharCount] = useState(0);
  const [answerCharCount, setAnswerCharCount] = useState(0);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return thirtyDaysAgo.toISOString().split('T')[0];
  });
  const [finalDate, setFinalDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [meta, setMeta] = useState<MetaType>({
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    page: 1,
    pageCount: 1,
    take: 10,
  });

  const getQuestions = async (startDateFilter?: string, endDateFilter?: string, take?: number, page?: number, search?: string) => {
    try {
      // Construir query string com filtros
      const params = new URLSearchParams();
      
      if (startDateFilter) {
        params.append('startDate', startDateFilter);
      }
      if (endDateFilter) {
        params.append('endDate', endDateFilter);
      }
      // Corrigir verificação para take - usar valor maior que 0
      if (take && take > 0) {
        params.append('take', take.toString());
      }
      // Corrigir verificação para page - usar valor maior que 0
      if (page && page > 0) {
        params.append('page', page.toString());
      }
      if (search) {
        params.append('search', search);
      }

      const queryString = params.toString();
      const endpoint = queryString ? `/knowledge-base/listAllQuestionsAnswers?${queryString}` : '/knowledge-base/listAllQuestionsAnswers';
      
      console.log('Fazendo requisição para:', endpoint);
      console.log('Parâmetros enviados:', { startDateFilter, endDateFilter, take, page, search });
      console.log('Query string:', queryString);
      
      const questionRes = await GET(endpoint);
      
      console.log('Resposta da API:', questionRes);
      
      // Verificar se a resposta tem a estrutura esperada
      if (questionRes && questionRes.data) {
        const newQuestions = questionRes.data.map((question: QuestionResponseType) => {
          return {
            id: question.id,
            prompt: question.prompt,
            completion: question.completion,
            created: question.created,
            status: question.status,
          }
        })
        setQuestions(newQuestions);
        
        // Atualizar meta se existir na resposta
        if (questionRes.meta) {
          setMeta({
            hasNextPage: questionRes.meta.hasNextPage || false,
            hasPreviousPage: questionRes.meta.hasPreviousPage || false,
            itemCount: questionRes.meta.itemCount || 0,
            page: questionRes.meta.page || 1,
            pageCount: questionRes.meta.pageCount || 1,
            take: questionRes.meta.take || 10,
          });
        }
      } else if (questionRes && Array.isArray(questionRes)) {
        // Caso a API retorne diretamente um array (sem wrapper)
        const newQuestions = questionRes.map((question: QuestionResponseType) => {
          return {
            id: question.id,
            prompt: question.prompt,
            completion: question.completion,
            created: question.created,
            status: question.status,
          }
        })
        setQuestions(newQuestions);
        
        // Se não há meta, usar valores padrão
        setMeta({
          hasNextPage: false,
          hasPreviousPage: false,
          itemCount: newQuestions.length,
          page: page || 1,
          pageCount: 1,
          take: take || 10,
        });
      } else {
        console.log('Estrutura de resposta inesperada:', questionRes);
        setQuestions([]);
      }
    } catch (e) {
      console.error('Erro ao buscar questões:', e);
      setQuestions([])
    }
  }

  // Função para filtrar por período
  const handleDateFilter = async () => {
    console.log('Aplicando filtro de data:', { startDate, finalDate });
    
    // Validar se as datas estão definidas
    if (!startDate || !finalDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Por favor, selecione uma data inicial e final para filtrar.',
      });
      return;
    }
    
    // Validar se a data inicial não é maior que a final
    if (new Date(startDate) > new Date(finalDate)) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'A data inicial não pode ser maior que a data final.',
      });
      return;
    }
    
    setLoading(true);
    try {
      // Limpar dados atuais antes de fazer nova busca
      setQuestions([]);
      
      // Reset para primeira página quando aplicar filtro
      await getQuestions(startDate, finalDate, meta.take, 1, '');
      
      console.log('Filtro aplicado com sucesso');
    } catch (error) {
      console.error('Erro ao aplicar filtro de data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao aplicar filtro de data. Tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para lidar com mudanças na paginação
  const onChangeData = async (take: number, page: number, search: string) => {
    setLoading(true);
    try {
      await getQuestions(startDate, finalDate, take, page, search);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const createQuestion = async () => {
    try {
      if (!answer || !question) {
        Swal.fire({
          icon: 'error',
          title: 'Campos faltantes',
          text: 'Verifique se preencheu todos os campos!',
        })
      } else {
        setLoading(true)
        const questionRes = await POST('/knowledge-base/createQuestionsAnswers', {
          prompt: question,
          completion: answer,
          status: true,
        })
        if (questionRes.success === true) {
          Swal.fire({
            icon: 'success',
            title: 'Treinamento cadastrado',
            text: 'Cadastro realizado com sucesso!',
          }).then(async () => await init())
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Erro ao cadastrar treinamento!',
          }).then(async () => await init())
        }
        setLoading(false)
      }
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao cadastrar treinamento!',
      }).then(async () => await init())
      console.log(e)
    }
  }

  const switchStatus = async (Id: number, status: boolean) => {
    try {
      const newQuestions = questions.map((question) => {
        if (question.id === Id) {
          question.status = !status
        }
        return question
      })
      setQuestions(newQuestions)
      await PATCH(`/knowledge-base/updateQuestionsAnswers/${Id}`, {
        status: !status,
      })
    } catch (e) {
      console.log(e)
    }
  }

  const editQuestions = async () => {
    try {
      if (!answer || !question) {
        Swal.fire({
          icon: 'error',
          title: 'Campos faltantes',
          text: 'Verifique se preencheu todos os campos!',
        })
      } else {
        setLoading(true)
        const questionsRes = await PATCH(`/knowledge-base/updateQuestionsAnswers/${id}`, {
          prompt: question,
          completion: answer,
        })
        if (questionsRes.success === true) {
          Swal.fire({
            icon: 'success',
            title: 'Treinamento alterado',
            text: 'Edição realizada com sucesso!',
          }).then(async () => await init())
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Erro ao alterar treinamento!',
          }).then(async () => await init())
        }
        setLoading(false)
      }
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao alterar treinamento!',
      }).then(async () => await init())
      console.log(e)
    }
  }

  const deleteQuestion = async () => {
    try {
      setLoading(true)
      const questionRes = await DELETE(`/knowledge-base/removeQuestionsAnswers/${id}`)
      if (questionRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: 'Treinamento deletado',
          text: 'Deleção realizada com sucesso!',
        }).then(async () => await init())
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao deletar treinamento!',
        }).then(async () => await init())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao deletar treinamento!',
      }).then(async () => await init())
      console.log(e)
    }
  }

  const adjustHeightQuestion = () => {
    const textbox = document.getElementById('question-modal')
    if (textbox) {
      if (textbox.scrollHeight <= 130) {
        textbox.style.height = 'inherit'
        textbox.style.height = `${textbox.scrollHeight}px`
      }
    }
  }

  const adjustHeightAnswer = () => {
    const textbox = document.getElementById('answer-modal')
    if (textbox) {
      if (textbox.scrollHeight <= 180) {
        textbox.style.height = 'inherit'
        textbox.style.height = `${textbox.scrollHeight}px`
      }
    }
  }

  // Função para detectar scroll e mostrar/ocultar barra fixa
  const handleToolbarScroll = () => {
    const cardHeader = document.querySelector('.card-header');
    if (cardHeader) {
      const headerRect = cardHeader.getBoundingClientRect();
      // Mostra a barra fixa quando o header original sair da tela (posição negativa)
      setShowFixedToolbar(headerRect.bottom < 0);
    }
  };

  // Funções para atualizar contadores de caracteres
  const handleQuestionChange = (value: string) => {
    setQuestion(value);
    setQuestionCharCount(value.length);
  };

  const handleAnswerChange = (value: string) => {
    setAnswer(value);
    setAnswerCharCount(value.length);
  };

  const verifyPlugins = async () => {
    const templateRes = await GET('/plugintemplate/ia')
    let hasPlugin = false
    if (templateRes && templateRes.data) {
      templateRes.data.forEach((template: TemplateType) => {
        if (template.pluginconfig) {
          if (
            template.pluginconfig.content &&
            template.pluginconfig.content.able &&
            template.pluginconfig.content.able !== '0'
          ) {
            setAgentName(template.pluginconfig.content.agentName)
            setConfig(template.pluginconfig)
            hasPlugin = true
          }
        }
      })
    }
    return hasPlugin
  }

const syncIA = async () => {
  try {
    setLoading(true);
    
    
    const resAgent = await GET(`agent-ia/listAgentNIS/${agentName}`);
    
    if (!resAgent.agentId) {
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Erro ao sincronizar',
        text: 'Falha ao recuperar o agente',
      });
      return;
    }

    // 1. Inicia o processo de sincronismo no backend
     const startResponse = await POST(`/knowledge-base/startSyncKB`,
      { agentId: resAgent.agentId }
    );

    if (!startResponse.success || !startResponse.processId) {
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Erro ao iniciar sincronismo',
        text: 'Falha ao iniciar o processo de sincronismo',
      });
      return;
    }

    if (startResponse.success && startResponse.processId) {
      setProcessId(startResponse.processId);
    }

    // 2. Inicia o polling quando recebe o processId do backend
    startStatusPolling(startResponse.processId);

    // 3. Mostra mensagem inicial para o usuário
    Swal.fire({
      icon: 'info',
      title: 'Sincronismo iniciado',
      text: 'O processo foi iniciado e você será notificado quando concluir.',
      timer: 10000,
      showConfirmButton: false
    });

    setLoading(false);

  } catch (error: any) {
    setLoading(false);
    Swal.fire({
      icon: 'error',
      title: 'Erro ao sincronizar',
      text: error.message || 'Falha ao sincronizar agente de IA',
    });
  }
};

// Função para verificar status
const checkSyncStatus = async (processId: string) => {
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Tempo limite excedido')), 8000) // 8000ms = 8 segundos
    );

    const response = await POST(`/knowledge-base/syncCheckStatus`,
      { processId:  processId }
    );

    const res = await Promise.race([response, timeoutPromise]);

    return res.data;
  } catch (error) {
    setLoading(false);
    Swal.fire({
      icon: 'error',
      title: 'Erro ao verificar status',
      text: 'Falha ao verificar o status do processo de sincronismo, aguarde alguns instantes...',
      timer: 3000,
    });
    console.error('Erro ao verificar status:', error);
    return null;
  }
};

  // Controle de página ativa
  useEffect(() => {
      setIsActivePage(true);
      return () => {
        setIsActivePage(false);
      };
  }, []);

  // Controle do polling
  useEffect(() => {
    if (processId) {
      const cleanup = startStatusPolling(processId);
      return () => cleanup();
    }
  }, [processId]);

// Gerencia o polling do status
const startStatusPolling = (processId: string) => {
  const interval = setInterval(async () => {

    // Só continua se estiver na página correta
      if (!isActivePage) {
        clearInterval(interval);
        return;
      }
    // Verifica o status do sincronismo
    const status = await checkSyncStatus(processId);
    
    if (status) {
      switch (status.currentStep || status.status) {
        case 'completed':
            clearInterval(interval);
            if (isActivePage) {
              Swal.fire({
                icon: 'success',
                title: 'Sincronismo concluído',
                text: 'O agente foi sincronizado com sucesso!'
              });
            }
        break;
          
        case 'failed':
            clearInterval(interval);
            if (isActivePage) {
              Swal.fire({
                icon: 'error',
                title: 'Falha no sincronismo',
                text: status.error || 'Ocorreu um erro durante o sincronismo'
              });
            }
            break;
          
        // Primeiro, crie o modal uma única vez quando iniciar o processo
case 'processing':
  if (isActivePage) {
    if (!Swal.isVisible()) {
      Swal.fire({
        title: 'Sincronismo em andamento',
        html: `
          <div id="sync-status-message" class="sync-status-message mb-4">
            ${status.message || 'Aguarde enquanto o processo de sincronização está em andamento.'}
          </div>
          <div class="progress" style="height: 24px; background: #e9ecef; border-radius: 8px; overflow: hidden;">
            <div 
              id="sync-progress-bar"
              class="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
              role="progressbar" 
              style="width: 0%; font-weight: 600; font-size: 14px;"
            >
              0%
            </div>
          </div>
          <div id="sync-status-details" class="sync-status-details mt-3 text-muted">
            <small>Processo iniciado...</small>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        customClass: {
          popup: 'sync-status-popup',
          title: 'sync-status-title'
        }
      });
    } else {
        // Apenas atualiza o conteúdo existente
        const messageElement = document.getElementById('sync-status-message');
        const progressBar = document.getElementById('sync-progress-bar');
        const detailsElement = document.getElementById('sync-status-details');

        if (messageElement && progressBar && detailsElement) {
          messageElement.innerHTML = 
            status.message || 'Aguarde enquanto o processo de sincronização está em andamento.';
          
          progressBar.style.width = `${status.progress}%`;
          progressBar.innerHTML = `${status.progress}%`;

          if (status.progress === 100) {
            detailsElement.innerHTML = 
              `<small>Processo concluído com sucesso!</small>`;
          }
        }
    }
  }
  break;
      }
    }
  }, 8000);

  // Define um timeout máximo (30 minutos)
  const timeout = setTimeout(() => {
    clearInterval(interval);
    if (isActivePage) {
      Swal.fire({
        icon: 'warning',
        title: 'Tempo limite excedido',
        text: 'O processo de sincronização excedeu o tempo limite de 30 minutos.'
      });
    }
  }, 30 * 60 * 1000);

  // Retorna função de limpeza para quando o componente for desmontado
  return () => {
    clearInterval(interval);
    clearTimeout(timeout);
  };
};

// Função para atualizar o progresso na interface (opcional)
const updateProgress = (progress: number) => {
  // Aqui você pode atualizar uma barra de progresso ou outro elemento visual
  const progressElement = document.getElementById('sync-progress');
  if (progressElement) {
    progressElement.style.width = `${progress}%`;
  }
};


  const init = async () => {
    setLoading(true)
    const hasPlugin = await verifyPlugins()
    if (!hasPlugin) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'É necessário estar com um plugin de IA habilitado',
      }).then(() => navigate('/ia/config'))
      setLoading(false)
      return
    }
    // Usar um valor padrão seguro para take (10 itens por página)
    const defaultTake = meta.take > 0 ? meta.take : 10
    await getQuestions(startDate, finalDate, defaultTake, 1, '')
    setLoading(false)
  }

  // Função específica para recarregar/atualizar a lista
  const reloadQuestions = async () => {
    setLoading(true)
    try {
      console.log('Recarregando lista completa...')
      console.log('Estado atual do meta:', meta)
      
      // Usar valores padrão maiores para carregar mais itens
      const defaultTake = 50  // Aumentar para 50 itens por página
      const defaultPage = 1
      
      // Resetar meta para valores padrão antes da chamada
      setMeta({
        hasNextPage: false,
        hasPreviousPage: false,
        itemCount: 0,
        page: defaultPage,
        pageCount: 1,
        take: defaultTake,
      })
      
      // Chamar getQuestions sem filtros de data para carregar tudo
      console.log('Chamando getQuestions sem filtros de data para carregar todos os itens...')
      await getQuestions(undefined, undefined, defaultTake, defaultPage, '')
      console.log('Lista recarregada com sucesso')
      
      Swal.fire({
        icon: 'success',
        title: 'Lista Recarregada',
        text: 'A lista foi atualizada com sucesso!',
        timer: 2000,
        showConfirmButton: false
      })
      
    } catch (error) {
      console.error('Erro ao recarregar lista:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao recarregar a lista. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    adjustHeightQuestion()
  }, [question])

  useEffect(() => {
    adjustHeightAnswer()
  }, [answer])

  // UseEffects para atualizar contadores de caracteres
  useEffect(() => {
    setQuestionCharCount(question.length);
  }, [question]);

  useEffect(() => {
    setAnswerCharCount(answer.length);
  }, [answer]);

  useEffect(() => {
    init()
  }, [])

  // UseEffect para detectar scroll e controlar barra fixa
  useEffect(() => {
    window.addEventListener('scroll', handleToolbarScroll);
    handleToolbarScroll(); // Executa uma vez para definir estado inicial
    
    return () => {
      window.removeEventListener('scroll', handleToolbarScroll);
    };
  }, []);

  return (
    <>
      {loading ? (
        <div 
        className='d-flex flex-column align-items-center justify-content-center' 
        style={{ 
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Loading />
        <span className='text-muted fs-6 fw-bold mt-3'>
          Aguarde alguns instantes, processamento em andamento...
        </span>
      </div>
      ) : (
        <div className='row g-5 gx-xxl-12' style={{ paddingTop: showFixedToolbar ? '80px' : '0' }}>
           {/* .Card inicial - conteúdo do card ... */}
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>{i18n.t(`chat.fit`)}</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>{i18n.t(`chat.fit_header`)}</span>
              </h3>
              <div className='card-toolbar gap-2'>
                <Button
                  text={i18n.t(`chat.add_question`)}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => {
                    showWrapperModal(i18n.t(`chat.add_question`))
                    setQuestion('')
                    setAnswer('')
                    setId(null)
                    setAction('creating')
                    setQuestionCharCount(0)
                    setAnswerCharCount(0)
                  }}
                  btnClass='mb-5 fw-bolder'
                />
                <Button
                  text={i18n.t(`chat.sync`)}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-cloud'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={async () => await syncIA()}
                  btnClass='mb-5 fw-bolder'
                />
                {/* <Button
                  text={i18n.t(`chat.upload`)}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-cloud'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={async () => await syncIA()}
                  btnClass='mb-5 fw-bolder'
                /> */}
              </div>
            </div>
            <div className='card-body'>
              {/* Filtros de Data - Posicionados como solicitado */}
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <div className='d-flex gap-3 align-items-end'>
                  <div className='d-flex flex-column'>
                    <label className='form-label fs-7 fw-bold text-gray-700 mb-2'>
                      {i18n.t(`permissions.ia_initial_date`) || 'Data Inicial'}
                    </label>
                    <Input
                      inputAttr={{
                        type: 'date',
                        value: startDate,
                      }}
                      change={(value: string) => setStartDate(value)}
                      placeholder="Data inicial"
                      size="small"
                    />
                  </div>
                  <div className='d-flex flex-column'>
                    <label className='form-label fs-7 fw-bold text-gray-700 mb-2'>
                      {i18n.t(`permissions.ia_final_date`) || 'Data Final'}
                    </label>
                    <Input
                      inputAttr={{
                        type: 'date',
                        value: finalDate,
                      }}
                      change={(value: string) => setFinalDate(value)}
                      placeholder="Data final"
                      size="small"
                    />
                  </div>
                  <div className='d-flex flex-column'>
                    <Button
                      text={i18n.t(`permissions.ia_filter`)}
                      color='info'
                      iconColor='#FFF'
                      icon='fas fa-filter'
                      size='small'
                      horizontalPadding={4}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={handleDateFilter}
                      btnClass='fw-bolder'
                    />
                  </div>
                  <div className='d-flex flex-column'>
                    <Button
                      text={i18n.t(`permissions.ia_recharge`)}
                      color='success'
                      iconColor='#FFF'
                      icon='fas fa-sync'
                      size='small'
                      horizontalPadding={4}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => reloadQuestions()}
                      btnClass='fw-bolder'
                    />
                  </div>
                </div>
              </div>

              <Datatable
                data={questions}
                meta={meta}
                onChangeData={onChangeData}
                headers={[
                  //   {key: 'Id', value: 'id', type: 'default'},
                  {
                    key: i18n.t(`chat.question`),
                    value: 'prompt',
                    type: 'default',
                    noLimit: true,
                    noOrder: true,
                  },
                  {
                    key: i18n.t(`chat.answer`),
                    value: 'completion',
                    type: 'default',
                    noLimit: true,
                    noOrder: true,
                  },
                  {key: i18n.t(`chat.date`), value: 'created', type: 'datetime'},
                  {
                    key: i18n.t(`chat.active`),
                    value: 'status',
                    type: 'check',
                    action: (item) => switchStatus(item.id, item.status),
                    noOrder: true,
                  },
                  {key: i18n.t(`chat.actions`), value: 'acoes', type: '', noOrder: true},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      showWrapperModal(`${i18n.t(`chat.edit_question`)}`)
                      setAction('editing')
                      setId(item.id)
                      setQuestion(item.prompt)
                      setAnswer(item.completion)
                      setQuestionCharCount(item.prompt.length)
                      setAnswerCharCount(item.completion.length)
                    },
                    title: `${i18n.t(`chat.edit_question`)}`,
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showWrapperModal(`${i18n.t(`chat.delete_question`)}`)
                      setAction('deleting')
                      setId(item.id)
                      setQuestion(item.prompt)
                      setAnswer(item.completion)
                      setQuestionCharCount(item.prompt.length)
                      setAnswerCharCount(item.completion.length)
                    },
                    title: `${i18n.t(`chat.delete_question`)}`,
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          {/* Adicione a barra de progresso aqui */}
          <div className={styles['progress-bar-wrapper']} style={{display: loading ? 'block' : 'none'}}>
            <div 
              id="sync-progress" 
              className={styles['progress-bar']} 
              role="progressbar"
              style={{width: '0%'}}
            />
            <span className={styles['progress-text']}>Sincronizando...</span>
          </div>

          <ModalWrapper
            textAction={action === 'deleting' ? i18n.t(`chat.delete`) : i18n.t(`buttons.save`)}
            confirmAction={
              action === 'creating'
                ? () => createQuestion()
                : action === 'editing'
                ? () => editQuestions()
                : () => deleteQuestion()
            }
          >
            {action === 'deleting' ? (
              <div className='w-100 d-flex flex-column'>
                <div className='w-100 d-flex flex-row justify-content-center'>
                  <svg
                    width='204'
                    height='197'
                    viewBox='0 0 204 197'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <g clip-path='url(#clip0_3409_3720)'>
                      <path
                        d='M159.301 0.286077L9.67877 32.3076C3.18191 33.698 -0.957669 40.0919 0.432766 46.5888L30.4222 186.716C31.8126 193.212 38.2065 197.352 44.7034 195.962L194.325 163.94C200.822 162.55 204.962 156.156 203.571 149.659L173.582 9.53208C172.191 3.03522 165.797 -1.10436 159.301 0.286077Z'
                        fill='#E8EDFC'
                      />
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M165.34 146.75C167 147.19 168.48 149.15 168.65 151.13C168.82 153.11 167.62 154.36 165.96 153.92C164.3 153.48 162.82 151.52 162.65 149.54C162.48 147.56 163.68 146.31 165.34 146.75Z'
                        fill='#BDD0FB'
                      />
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M138.12 144.13C140.97 144.88 143.52 148.26 143.82 151.67C144.12 155.08 142.04 157.23 139.19 156.48C136.34 155.73 133.79 152.35 133.49 148.94C133.19 145.53 135.27 143.38 138.12 144.13Z'
                        fill='#BDD0FB'
                      />
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M166.67 173.59C167.77 173.88 168.75 175.18 168.86 176.49C168.97 177.8 168.18 178.62 167.08 178.34C165.99 178.05 165.01 176.75 164.89 175.44C164.78 174.13 165.57 173.3 166.67 173.59Z'
                        fill='#BDD0FB'
                      />
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M196.92 139.82C198.02 140.11 199 141.41 199.11 142.72C199.22 144.03 198.43 144.86 197.33 144.57C196.24 144.28 195.26 142.98 195.14 141.68C195.03 140.37 195.82 139.54 196.92 139.83V139.82Z'
                        fill='#BDD0FB'
                      />
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M155.85 119.09C156.95 119.38 157.93 120.68 158.04 121.99C158.15 123.3 157.36 124.13 156.26 123.84C155.16 123.55 154.18 122.25 154.07 120.94C153.96 119.63 154.75 118.8 155.85 119.09Z'
                        fill='#BDD0FB'
                      />
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M118.11 146.98C119.21 147.27 120.18 148.57 120.3 149.88C120.42 151.19 119.62 152.02 118.52 151.73C117.42 151.44 116.44 150.14 116.33 148.83C116.22 147.52 117.01 146.69 118.11 146.98Z'
                        fill='#BDD0FB'
                      />
                      <path
                        d='M91.5099 42.4897H69.1399C67.5999 42.4897 66.3599 41.2397 66.3599 39.7097C66.3599 38.1697 67.6099 36.9297 69.1399 36.9297H91.5099C93.0499 36.9297 94.2899 38.1797 94.2899 39.7097C94.2899 41.2497 93.0399 42.4897 91.5099 42.4897Z'
                        fill='#BDD0FB'
                      />
                      <path
                        d='M75.03 31.5795H3.12998C1.58998 31.5795 0.349976 30.3295 0.349976 28.7995C0.349976 27.2595 1.59998 26.0195 3.12998 26.0195H75.04C76.58 26.0195 77.82 27.2695 77.82 28.7995C77.82 30.3395 76.57 31.5795 75.04 31.5795H75.03Z'
                        fill='#BDD0FB'
                      />
                      <path
                        d='M59.06 42.4799H11.77C10.23 42.4799 8.98999 41.2299 8.98999 39.6999C8.98999 38.1599 10.24 36.9199 11.77 36.9199H59.06C60.6 36.9199 61.84 38.1699 61.84 39.6999C61.84 41.2399 60.59 42.4799 59.06 42.4799Z'
                        fill='#BDD0FB'
                      />
                      <path
                        d='M63.23 64.27H11.77C10.23 64.27 8.98999 63.02 8.98999 61.49C8.98999 59.95 10.24 58.71 11.77 58.71H63.23C64.77 58.71 66.01 59.96 66.01 61.49C66.01 63.03 64.76 64.27 63.23 64.27Z'
                        fill='#BDD0FB'
                      />
                      <path
                        d='M91.7401 53.3696H19.83C18.29 53.3696 17.05 52.1196 17.05 50.5896C17.05 49.0496 18.3 47.8096 19.83 47.8096H91.7401C93.2801 47.8096 94.5201 49.0596 94.5201 50.5896C94.5201 52.1296 93.2701 53.3696 91.7401 53.3696Z'
                        fill='#BDD0FB'
                      />
                      <path
                        d='M70.7099 138.03H70.6299C63.5099 137.79 57.9199 132.02 57.9199 124.89C57.9199 122.77 58.4399 120.65 59.4399 118.77C59.4399 118.77 59.4999 118.65 59.5199 118.63L90.9699 64.1504C90.9699 64.1504 91.0199 64.0704 91.0499 64.0204L91.1599 63.8704C93.6199 60.3104 97.6699 58.1904 101.99 58.1904C106.31 58.1904 110.36 60.3104 112.82 63.8704L112.93 64.0204C112.93 64.0204 112.98 64.1004 113.01 64.1404L144.63 118.91C144.63 118.91 144.68 118.99 144.7 119.03L144.77 119.18C145.64 120.97 146.08 122.9 146.08 124.89C146.08 132.02 140.49 137.79 133.36 138.03H70.7099Z'
                        fill='#EAB83B'
                      />
                      <path
                        d='M139.04 121.951L138.51 121.031L108.13 68.4108L107.6 67.5008C106.37 65.7208 104.32 64.5508 102 64.5508C99.68 64.5508 97.62 65.7208 96.39 67.5008L95.86 68.4108L65.27 121.411L65.07 121.751C64.57 122.691 64.29 123.761 64.29 124.911C64.29 128.581 67.21 131.561 70.85 131.691H133.15C136.79 131.571 139.71 128.591 139.71 124.911C139.71 123.851 139.46 122.851 139.03 121.961L139.04 121.951Z'
                        fill='white'
                      />
                      <path
                        d='M101.99 108.561C98.97 108.561 96.52 106.111 96.52 103.091V87.0608C96.52 84.0408 98.96 81.5908 101.99 81.5908C105.02 81.5908 107.45 84.0408 107.45 87.0608V103.091C107.45 106.111 105 108.561 101.99 108.561Z'
                        fill='#6E80FF'
                      />
                      <path
                        d='M107.43 115.85C107.43 112.84 105 110.4 101.99 110.4C98.98 110.4 96.54 112.84 96.54 115.85C96.54 118.86 98.98 121.29 101.99 121.29C105 121.29 107.43 118.85 107.43 115.85Z'
                        fill='#6E80FF'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_3409_3720'>
                        <rect
                          width='203.68'
                          height='196.23'
                          fill='white'
                          transform='translate(0.160034)'
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div className='w-100 d-flex flex-column align-items-start mt-4 gap-2'>
                  <b>{question}</b>
                  <p>{answer}</p>
                </div>
              </div>
            ) : (
              <div className='d-flex flex-column flex-wrap gap-5'>
                <div className='flex-grow-1'>
                  <Input
                    idInput='question-modal'
                    inputAttr={{
                      type: 'textarea',
                    }}
                    value={question}
                    change={handleQuestionChange}
                    placeholder={i18n.t(`chat.question`)}
                    label={i18n.t(`chat.question`)}
                    rows={2}
                  />
                  <div className="text-end mt-1">
                    <small className="text-muted">
                      {questionCharCount} caracteres
                    </small>
                  </div>
                </div>
                <div className='flex-grow-1'>
                  <Input
                    idInput='answer-modal'
                    inputAttr={{
                      type: 'textarea',
                    }}
                    rows={5}
                    value={answer}
                    change={handleAnswerChange}
                    placeholder={i18n.t(`chat.answer`)}
                    label={i18n.t(`chat.answer`)}
                  />
                  <div className="text-end mt-1">
                    <small className="text-muted">
                      {answerCharCount} caracteres
                    </small>
                  </div>
                </div>
              </div>
            )}
          </ModalWrapper>

          {/* Barra de botões fixa */}
          {showFixedToolbar && (
            <div
              className="position-fixed w-100"
              style={{
                top: '0',
                left: '0',
                zIndex: 1040,
                backgroundColor: 'white',
                borderBottom: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                padding: '15px 20px'
              }}
            >
              <div className="container-fluid">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold text-primary">
                    {i18n.t(`chat.fit`)} - Treinamento IA
                  </h5>
                  <div className="d-flex gap-2">
                    <Button
                      text={i18n.t(`chat.add_question`)}
                      color='primary'
                      iconColor='#FFF'
                      icon='fas fa-plus'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => {
                        showWrapperModal(i18n.t(`chat.add_question`))
                        setQuestion('')
                        setAnswer('')
                        setId(null)
                        setAction('creating')
                        setQuestionCharCount(0)
                        setAnswerCharCount(0)
                      }}
                      btnClass='fw-bolder'
                    />
                    <Button
                      text={i18n.t(`chat.sync`)}
                      color='primary'
                      iconColor='#FFF'
                      icon='fas fa-cloud'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={async () => await syncIA()}
                      btnClass='fw-bolder'
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

const Fit: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: i18n.t(`chat.ia`), path: 'ia/fit', isActive: true},
        ]}
      >
        {i18n.t(`chat.fit`)}
      </PageTitle>
      <FitContent />
    </>
  )
}

export {Fit}
